import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { Alert, Container, Row, Col, Card, Badge, Button, Form, Modal } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FaArrowRight,
  FaPaperclip, 
  FaDownload, 
  FaUserPlus, 
  FaUsers,
  FaBuilding,
  FaCalendarAlt,
  FaTag,
  FaClock,
  FaTrashAlt,
  FaPaperPlane,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEnvelopeOpenText,
  FaSave,
  FaTimes,
  FaSearch,
  FaCheck,
  FaChevronDown,
  FaEdit,
  FaStar
} from 'react-icons/fa';
import LoadingSpinner from '../components/LoadingSpinner';
import AssignableUsersSelect from '../components/AssignableUsersSelect';
import ticketService from '../services/ticketService';
import userService from '../services/userService';
import branchService from '../services/branchService';
import { AuthContext } from '../context/AuthContext';
import { formatDateTime, formatNumber } from '../utils/format';
import { getRoleLabel, ROLE, normalizeRole, isViewer, canWriteTickets, canRateTechnician, canManageExceptionalWork } from '../utils/rbac';
import { mapAssignedUsersToOptions, normalizeAssignableUserOptions } from '../utils/assignableUsers';
import { translateIssueType, translateIssueTypes } from '../utils/issueTypes';
import {
  buildApiErrorMessage,
  buildTicketDateTimePayload,
  splitTicketDateTime,
  UPLOAD_ACCEPT_ATTRIBUTE,
  validateTicketUploadFile
} from '../utils/ticketForm';

// ────────────────────────────────────────────────────────────
//  PURE HELPERS (outside component — no re-creation per render)
// ────────────────────────────────────────────────────────────
const STATUS_MAP = {
  new: { label: 'تذكرة جديدة', cssClass: 'status-new' },
  in_progress: { label: 'قيد المتابعة', cssClass: 'status-in-progress' },
  on_hold: { label: 'متوقفة مؤقتاً', cssClass: 'status-on-hold' },
  resolved: { label: 'محلولة', cssClass: 'status-resolved' },
  closed: { label: 'مغلقة كلياً', cssClass: 'status-closed' }
};
const PRIORITY_MAP = {
  low: { label: 'منخفضة الأولوية', cssClass: 'priority-low' },
  medium: { label: 'أولوية متوسطة', cssClass: 'priority-medium' },
  high: { label: 'أولوية عالية', cssClass: 'priority-high' },
  urgent: { label: 'أولوية قصوى', cssClass: 'priority-urgent' }
};

const ROLE_LABELS = {
  admin: 'مدير النظام',
  supervisor: 'مشرف', technician: 'فني'
};

const StatusBadge = React.memo(({ status }) => {
  const config = STATUS_MAP[status] || { label: status, cssClass: 'status-on-hold' };
  return <span className={`status-badge-premium ${config.cssClass}`}>{config.label}</span>;
});

const PriorityBadge = React.memo(({ priority }) => {
  const config = PRIORITY_MAP[priority] || { label: priority, cssClass: 'priority-medium' };
  return <span className={`priority-badge-premium ${config.cssClass}`}>{config.label}</span>;
});

// SLA Status Badge Component
const SlaStatusBadge = React.memo(({ slaStatus }) => {
  if (!slaStatus || slaStatus === 'n/a') {
    return <span className="sla-badge sla-na">غير مُطبق</span>;
  }
  
  const slaMap = {
    on_track: { label: 'مقبول', class: 'sla-on-track' },
    at_risk: { label: 'مخاطر', class: 'sla-at-risk' },
    breached: { label: 'متأخر', class: 'sla-breached' },
    met: { label: 'محقق', class: 'sla-met' }
  };
  
  const config = slaMap[slaStatus] || { label: slaStatus, class: 'sla-na' };
  
  return <span className={`sla-badge-premium ${config.class}`}>{config.label}</span>;
});


// ────────────────────────────────────────────────────────────
//  INLINE MULTI-SELECT TECH ASSIGNMENT COMPONENT
//  Dropdown-style, searchable, with chips — no modal needed
// ────────────────────────────────────────────────────────────
const TechnicianAssigner = React.memo(({
  technicians,         // full list of assignable technicians
  assignedTechnicians, // currently assigned techs [{id, full_name, is_primary, ...}]
  ticketId,
  onAssignmentChanged, // callback after save
  canManage,
  submitting: parentSubmitting
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [localSelected, setLocalSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Sync local selection when assigned technicians change
  useEffect(() => {
    setLocalSelected(assignedTechnicians.map(t => t.id));
  }, [assignedTechnicians]);

  // When opening, focus search
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // Filter technicians by search — memoized
  const filteredTechnicians = useMemo(() => {
    if (!Array.isArray(technicians)) return [];
    if (!search.trim()) return technicians;
    const q = search.trim().toLowerCase();
    return technicians.filter(t =>
      (t.full_name || '').toLowerCase().includes(q) ||
      (t.username || '').toLowerCase().includes(q) ||
      (t.email || '').toLowerCase().includes(q)
    );
  }, [technicians, search]);

  const toggleTech = useCallback((techId) => {
    setLocalSelected(prev =>
      prev.includes(techId) ? prev.filter(id => id !== techId) : [...prev, techId]
    );
  }, []);

  const hasChanges = useMemo(() => {
    const currentIds = assignedTechnicians.map(t => t.id).sort((a, b) => a - b);
    const selectedIds = [...localSelected].sort((a, b) => a - b);
    if (currentIds.length !== selectedIds.length) return true;
    return currentIds.some((id, i) => id !== selectedIds[i]);
  }, [assignedTechnicians, localSelected]);

  const handleSave = useCallback(async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    try {
      await ticketService.assignTechnicians(ticketId, localSelected, true);
      setIsOpen(false);
      setSearch('');
      if (onAssignmentChanged) onAssignmentChanged();
    } catch (err) {
      console.error('Assign technicians error:', err);
      alert(err.response?.data?.error || err.message || 'فشل تعيين الفنيين');
    } finally {
      setSaving(false);
    }
  }, [hasChanges, saving, ticketId, localSelected, onAssignmentChanged]);

  const handleRemove = useCallback(async (techId) => {
    if (!window.confirm('هل أنت متأكد من إزالة هذا الفني من التذكرة؟')) return;
    setRemoving(techId);
    try {
      await ticketService.removeTechnician(ticketId, techId);
      if (onAssignmentChanged) onAssignmentChanged();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'فشل إزالة الفني');
    } finally {
      setRemoving(null);
    }
  }, [ticketId, onAssignmentChanged]);

  const isAssigned = useCallback((techId) => {
    return assignedTechnicians.some(t => t.id === techId);
  }, [assignedTechnicians]);

  return (
    <div className="ticket-card-premium">
      <div className="ticket-card-premium-header">
        <h5 className="ticket-card-premium-title team-members-title">
          <FaUsers className="text-primary" style={{fontSize: '0.9rem'}} />
          <span className="team-members-title-text">فريق العمل</span>
        </h5>
        {assignedTechnicians.length > 0 && (
          <Badge bg="light" text="dark" className="rounded-pill border" style={{fontSize: '0.72rem'}}>
            {assignedTechnicians.length}
          </Badge>
        )}
      </div>
      <div className="ticket-card-premium-body" style={{padding: '1rem 1.25rem'}}>
        {/* Assigned technicians chips */}
        <div className="td-tech-chips">
          {assignedTechnicians.length > 0 ? (
            assignedTechnicians.map(tech => (
              <div key={tech.id} className="td-tech-chip">
                <div className="td-tech-chip-avatar">{(tech.full_name || '?').charAt(0)}</div>
                <div className="td-tech-chip-info">
                  <span className="td-tech-chip-name">{tech.full_name}</span>
                  <span className="td-tech-chip-role">
                    {tech.is_primary ? 'الفني الأساسي' : 'فني مشارك'}
                  </span>
                </div>
                {canManage && (
                  <button
                    className="td-tech-chip-remove"
                    onClick={() => handleRemove(tech.id)}
                    disabled={removing === tech.id || parentSubmitting}
                    title="إزالة من الفريق"
                  >
                    {removing === tech.id ? '...' : <FaTimes />}
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className="td-tech-empty">لم يتم تعيين فنيين بعد</div>
          )}
        </div>

        {/* Add/manage button + dropdown */}
        {canManage && (
          <div className="td-tech-dropdown-container" ref={containerRef}>
            <button
              className="td-tech-dropdown-trigger"
              onClick={() => {
                setIsOpen(!isOpen);
                if (!isOpen) {
                  setLocalSelected(assignedTechnicians.map(t => t.id));
                  setSearch('');
                }
              }}
              disabled={parentSubmitting}
            >
              <FaUserPlus style={{fontSize: '0.8rem'}} />
              <span>إدارة فريق العمل</span>
              <FaChevronDown className={`td-tech-dropdown-arrow ${isOpen ? 'rotated' : ''}`} />
            </button>

            {isOpen && (
              <div className="td-tech-dropdown-panel">
                {/* Search */}
                <div className="td-tech-search-wrap">
                  <FaSearch className="td-tech-search-icon" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="td-tech-search-input"
                    placeholder="بحث عن فني..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* List */}
                <div className="td-tech-list">
                  {filteredTechnicians.length === 0 ? (
                    <div className="td-tech-list-empty">لا توجد نتائج</div>
                  ) : (
                    filteredTechnicians.map(tech => {
                      const selected = localSelected.includes(tech.id);
                      const currentlyAssigned = isAssigned(tech.id);
                      return (
                        <div
                          key={tech.id}
                          className={`td-tech-list-item ${selected ? 'selected' : ''}`}
                          onClick={() => toggleTech(tech.id)}
                        >
                          <div className={`td-tech-list-check ${selected ? 'checked' : ''}`}>
                            {selected && <FaCheck />}
                          </div>
                          <div className="td-tech-list-avatar">
                            {(tech.full_name || '?').charAt(0)}
                          </div>
                          <div className="td-tech-list-info">
                            <span className="td-tech-list-name">{tech.full_name}</span>
                            <span className="td-tech-list-meta">
                              {ROLE_LABELS[normalizeRole(tech.role)] || tech.role}
                              {currentlyAssigned && ' • معيّن حالياً'}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Footer actions */}
                <div className="td-tech-dropdown-footer">
                  <span className="td-tech-footer-count">
                    {localSelected.length} فني محدد
                  </span>
                  <div className="td-tech-footer-actions">
                    <button
                      className="td-tech-footer-btn cancel"
                      onClick={() => {
                        setIsOpen(false);
                        setSearch('');
                      }}
                    >
                      إلغاء
                    </button>
                    <button
                      className="td-tech-footer-btn save"
                      onClick={handleSave}
                      disabled={!hasChanges || saving}
                    >
                      {saving ? 'جاري الحفظ...' : 'حفظ التعيينات'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

TechnicianAssigner.displayName = 'TechnicianAssigner';


// ────────────────────────────────────────────────────────────
//  MAIN TICKET DETAIL COMPONENT
// ────────────────────────────────────────────────────────────
const TicketAssigneeField = React.memo(({
  assignableUsers,
  assignedUsers,
  ticketId,
  onAssignmentChanged,
  canManage,
  submitting: parentSubmitting
}) => {
  const assignableUserIds = useMemo(
    () => new Set((Array.isArray(assignableUsers) ? assignableUsers : []).map((option) => option.value)),
    [assignableUsers]
  );
  const selectedOptions = useMemo(
    () => mapAssignedUsersToOptions(assignedUsers, assignableUsers),
    [assignedUsers, assignableUsers]
  );
  const [localSelected, setLocalSelected] = useState(selectedOptions);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalSelected(selectedOptions);
  }, [selectedOptions]);

  const hasChanges = useMemo(() => {
    const currentIds = selectedOptions.map((option) => option.value).sort((left, right) => left - right);
    const nextIds = localSelected.map((option) => option.value).sort((left, right) => left - right);

    if (currentIds.length !== nextIds.length) return true;

    return currentIds.some((id, index) => id !== nextIds[index]);
  }, [localSelected, selectedOptions]);

  const handleSave = useCallback(async (closeDropdown) => {
    if (!hasChanges || saving) return;

    setSaving(true);
    try {
      const payloadAssigneeIds = localSelected
        .map((option) => option.value)
        .filter((id) => assignableUserIds.has(id));

      await ticketService.assignUsers(
        ticketId,
        payloadAssigneeIds,
        true
      );
      closeDropdown?.();
      onAssignmentChanged?.();
    } catch (err) {
      console.error('Assign users error:', err);
      alert(err.response?.data?.error || err.message || 'فشل تحديث المكلفين بالتذكرة');
    } finally {
      setSaving(false);
    }
  }, [assignableUserIds, hasChanges, localSelected, onAssignmentChanged, saving, ticketId]);

  const handleReset = useCallback((closeDropdown) => {
    setLocalSelected(selectedOptions);
    closeDropdown?.();
  }, [selectedOptions]);

  return (
    <div className="ticket-card-premium">
      <div className="ticket-card-premium-header">
        <h5 className="ticket-card-premium-title team-members-title">
          <FaUsers className="text-primary" style={{fontSize: '0.9rem'}} />
          <span className="team-members-title-text">فريق العمل</span>
        </h5>
        {localSelected.length > 0 && (
          <Badge bg="light" text="dark" className="rounded-pill border" style={{fontSize: '0.72rem'}}>
            {localSelected.length}
          </Badge>
        )}
      </div>
      <div className="ticket-card-premium-body" style={{padding: '1rem 1.25rem'}}>
        <AssignableUsersSelect
          label="المكلفون بالتذكرة"
          options={assignableUsers}
          value={localSelected}
          onChange={setLocalSelected}
          placeholder="اختر الفنيين أو المشرفين أو مديري النظام"
          searchPlaceholder="ابحث بالاسم الكامل أو اسم المستخدم أو الصلاحية"
          noOptionsMessage="لا توجد نتائج مطابقة"
          noAvailableMessage="لا يوجد مستخدمون متاحون للتكليف"
          helperText="يمكنك إسناد هذه التذكرة إلى الفنيين والمشرفين ومديري النظام."
          disabled={!canManage || parentSubmitting || saving}
          renderFooter={({ closeDropdown }) => (
            <>
              <span className="tech-multiselect-footer-summary">
                {localSelected.length} محدد
              </span>
              <div className="tech-multiselect-footer-actions">
                <button
                  type="button"
                  className="tech-multiselect-action secondary"
                  onClick={() => handleReset(closeDropdown)}
                  disabled={saving}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="tech-multiselect-action primary"
                  onClick={() => handleSave(closeDropdown)}
                  disabled={!hasChanges || saving}
                >
                  {saving ? 'جارٍ الحفظ...' : 'حفظ المكلفين'}
                </button>
              </div>
            </>
          )}
        />
      </div>
    </div>
  );
});

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('attachment');
  const [fileError, setFileError] = useState('');
  const [resolutionAttachment, setResolutionAttachment] = useState(null);
  const [resolutionAttachmentError, setResolutionAttachmentError] = useState('');
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [showResolutionEmailModal, setShowResolutionEmailModal] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [resolutionEmailData, setResolutionEmailData] = useState({
    resolution_summary: '',
    closure_note: '',
    include_solution_attachment: false
  });
  const [updateData, setUpdateData] = useState({
    status: '',
    priority: '',
    resolution_summary: '',
    resolution_comment: '',
    custom_resolved_date: '',
    custom_resolved_time: '',
    has_overtime: false,
    overtime_type: '',
    overtime_duration: '',
    overtime_notes: '',
    rating: 0,
    rating_notes: '',
    has_exceptional_work: false,
    work_type: '',
    work_description: '',
    work_hours: '',
    estimated_cost: ''
  });
  const generalFileInputRef = useRef(null);
  const resolutionFileInputRef = useRef(null);
  const token = true;

  // Comment editing state
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  // Ticket edit state
  const [showTicketEditModal, setShowTicketEditModal] = useState(false);
  const [ticketEditData, setTicketEditData] = useState({
    status: '',
    priority: '',
    branch_id: '',
    received_date: '',
    received_time: '',
    resolved_date: '',
    resolved_time: '',
    short_description: '',
    issue_types: [],
    has_overtime: false,
    overtime_type: '',
    overtime_duration: '',
    overtime_notes: '',
    has_exceptional_work: false,
    work_type: '',
    work_description: '',
    work_hours: '',
    estimated_cost: '',
    resolution_notes: ''
  });
  const [branches, setBranches] = useState([]);
  const [ticketEditSubmitting, setTicketEditSubmitting] = useState(false);

  // ── Data fetching — memoized callbacks ──────────────────
  const fetchTicket = useCallback(async () => {
    try {
      const data = await ticketService.getById(id);
      const resolvedDateParts = splitTicketDateTime(data.resolved_at || data.resolved_date);
      setTicket(data);
      setUpdateData({
        status: data.status,
        priority: data.priority,
        resolution_summary: data.resolution_summary || data.resolution_notes || '',
        resolution_comment: '',
        custom_resolved_date: data.status === 'resolved' ? resolvedDateParts.date : '',
        custom_resolved_time: data.status === 'resolved' ? resolvedDateParts.time : '',
        has_overtime: data.has_overtime || false,
        overtime_type: data.overtime_type || '',
        overtime_duration: data.overtime_duration || '',
        overtime_notes: data.overtime_notes || '',
        rating: data.rating || 0,
        rating_notes: data.rating_notes || '',
        has_exceptional_work: data.has_exceptional_work || false,
        work_type: data.work_type || '',
        work_description: data.work_description || '',
        work_hours: data.work_hours || '',
        estimated_cost: data.estimated_cost || ''
      });
      setAssignedUsers(data.assignees || data.technicians || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAssignableUsers = useCallback(async () => {
    try {
      const data = await userService.getTicketAssignableUsers();
      const usersList = data?.users || data || [];
      setAssignableUsers(normalizeAssignableUserOptions(usersList));
    } catch (err) {
      console.error('Error fetching assignable users:', err);
      setAssignableUsers([]);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const data = await branchService.getAll();
      setBranches(data?.branches || data || []);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  }, []);

  // ── Initial load — parallel fetch ──────────────────────
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchTicket(), fetchAssignableUsers(), fetchBranches()]);
  }, [id, fetchAssignableUsers, fetchTicket, fetchBranches]);

  useEffect(() => {
    if (updateData.status === 'resolved') {
      return;
    }

    setResolutionAttachment(null);
    setResolutionAttachmentError('');

    if (resolutionFileInputRef.current) {
      resolutionFileInputRef.current.value = '';
    }
  }, [updateData.status]);

  // ── Handlers — all useCallback to prevent child re-renders ─
  const handleAddComment = useCallback(async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      await ticketService.addComment(id, { comment_text: commentText });
      setCommentText('');
      await fetchTicket();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }, [id, commentText, fetchTicket]);

  const handleEditComment = useCallback(async (commentId) => {
    if (!editingCommentText.trim()) return;
    setCommentSubmitting(true);
    try {
      await ticketService.updateComment(id, commentId, { comment_text: editingCommentText });
      setEditingCommentId(null);
      setEditingCommentText('');
      await fetchTicket();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'فشل تعديل التعليق');
    } finally {
      setCommentSubmitting(false);
    }
  }, [id, editingCommentText, fetchTicket]);

  const handleCancelEditComment = useCallback(() => {
    setEditingCommentId(null);
    setEditingCommentText('');
  }, []);

  // Ticket edit handlers
  const handleOpenTicketEdit = useCallback(() => {
    const receivedDateParts = splitTicketDateTime(ticket?.received_date);
    const resolvedDateParts = splitTicketDateTime(ticket?.resolved_date || ticket?.resolved_at);

    setTicketEditData({
      short_description: ticket?.short_description || '',
      priority: ticket?.priority || '',
      status: ticket?.status || '',
      branch_id: ticket?.branch_id || '',
      issue_types: ticket?.issue_types || [],
      received_date: receivedDateParts.date || '',
      received_time: receivedDateParts.time || '',
      resolved_date: resolvedDateParts.date || '',
      resolved_time: resolvedDateParts.time || '',
      has_overtime: ticket?.has_overtime || false,
      overtime_type: ticket?.overtime_type || '',
      overtime_duration: ticket?.overtime_duration || '',
      overtime_notes: ticket?.overtime_notes || '',
      has_exceptional_work: ticket?.has_exceptional_work || false,
      work_type: ticket?.work_type || '',
      work_description: ticket?.work_description || '',
      work_hours: ticket?.work_hours || '',
      estimated_cost: ticket?.estimated_cost || '',
      resolution_notes: ticket?.resolution_notes || ticket?.resolution_summary || ''
    });
    setShowTicketEditModal(true);
  }, [ticket]);

  const handleSaveTicketEdit = useCallback(async () => {
    if (!ticketEditData.short_description?.trim()) {
      alert('الرجاء إدخال عنوان التذكرة');
      return;
    }
    setTicketEditSubmitting(true);
    try {
      const updatePayload = {
        short_description: ticketEditData.short_description,
        priority: ticketEditData.priority,
        status: ticketEditData.status,
        issue_types: ticketEditData.issue_types,
        branch_id: ticketEditData.branch_id || null,
        resolution_notes: ticketEditData.resolution_notes || null,
        has_overtime: ticketEditData.has_overtime,
        has_exceptional_work: ticketEditData.has_exceptional_work
      };

      if (ticketEditData.received_date && ticketEditData.received_time) {
        updatePayload.received_date = new Date(`${ticketEditData.received_date}T${ticketEditData.received_time}:00`);
      }

      if ((ticketEditData.status === 'resolved' || ticketEditData.status === 'closed') && ticketEditData.resolved_date && ticketEditData.resolved_time) {
        updatePayload.resolved_date = new Date(`${ticketEditData.resolved_date}T${ticketEditData.resolved_time}:00`);
      }

      if (ticketEditData.has_overtime) {
        updatePayload.overtime_type = ticketEditData.overtime_type;
        updatePayload.overtime_duration = ticketEditData.overtime_duration;
        updatePayload.overtime_notes = ticketEditData.overtime_notes;
      }

      if (ticketEditData.has_exceptional_work) {
        updatePayload.work_type = ticketEditData.work_type;
        updatePayload.work_description = ticketEditData.work_description;
        updatePayload.work_hours = ticketEditData.work_hours;
        updatePayload.estimated_cost = ticketEditData.estimated_cost;
      }

      await ticketService.update(id, updatePayload);
      setShowTicketEditModal(false);
      await fetchTicket();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'فشل تعديل التذكرة');
    } finally {
      setTicketEditSubmitting(false);
    }
  }, [id, ticketEditData, fetchTicket]);

  const handleFileUpload = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('الرجاء اختيار ملف للرفع');
      return;
    }
    setSubmitting(true);
    try {
      if (fileType === 'solution') {
        await ticketService.uploadSolutionAttachment(id, selectedFile);
      } else {
        await ticketService.uploadAttachment(id, selectedFile);
      }
      setSelectedFile(null);
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      await fetchTicket();
      alert('تم رفع الملف بنجاح');
    } catch (err) {
      console.error('Upload error:', err);
      alert('خطأ: ' + (err.response?.data?.error || err.message || 'فشل رفع الملف'));
    } finally {
      setSubmitting(false);
    }
  }, [id, selectedFile, fileType, fetchTicket]);

  const handleDownloadSolution = useCallback(() => {
    if (!token) { alert('الرجاء تسجيل الدخول أولاً'); return; }
    ticketService.downloadSolutionAttachment(id).catch((error) => {
      alert(error.response?.data?.error || error.message || 'Failed to download the solution attachment');
    });
  }, [id]);

  const handleDownloadAttachment = useCallback((attachmentId) => {
    if (!token) { alert('الرجاء تسجيل الدخول أولاً'); return; }
    const attachment = (ticket?.attachments || []).find((item) => item.id === attachmentId);
    ticketService.downloadAttachment(id, attachmentId, attachment?.file_name).catch((error) => {
      alert(error.response?.data?.error || error.message || 'Failed to download the attachment');
    });
  }, [id, ticket]);

  const handleSecureSolutionDownload = useCallback(() => {
    ticketService.downloadSolutionAttachment(id).catch((error) => {
      alert(error.response?.data?.error || error.message || 'Failed to download the solution attachment');
    });
  }, [id]);

  const handleSecureAttachmentDownload = useCallback((attachmentId) => {
    const attachment = (ticket?.attachments || []).find((item) => item.id === attachmentId);
    ticketService.downloadAttachment(id, attachmentId, attachment?.file_name).catch((error) => {
      alert(error.response?.data?.error || error.message || 'Failed to download the attachment');
    });
  }, [id, ticket]);

  const handleUpdateTicket = useCallback(async () => {
    if (updateData.status === 'resolved' && !updateData.resolution_summary?.trim()) {
      alert('الرجاء إدخال ملاحظات الحل');
      return;
    }
    
    // Validate overtime fields
    // Overtime types 'holiday' and 'travel_over_100km' do not require duration
    const nonTimeOvertimeTypes = ['holiday', 'travel_over_100km'];
    const isNonTimeOvertime = nonTimeOvertimeTypes.includes(updateData.overtime_type);
    
    if ((updateData.status === 'resolved' || updateData.status === 'closed') && updateData.has_overtime) {
      if (!updateData.overtime_type) {
        alert('الرجاء اختيار نوع الوقت الإضافي');
        return;
      }
      // Only require duration for time-based overtime types
      if (!isNonTimeOvertime && !updateData.overtime_duration?.trim()) {
        alert('الرجاء إدخال مدة الوقت الإضافي');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const updatePayload = {
        status: updateData.status,
        priority: updateData.priority,
        resolution_notes: updateData.resolution_summary,
        resolved_date: null
      };
      if (updateData.status === 'resolved' && updateData.custom_resolved_date && updateData.custom_resolved_time) {
        updatePayload.resolved_date = new Date(`${updateData.custom_resolved_date}T${updateData.custom_resolved_time}:00`);
      }
      // Add overtime fields when resolving or closing a ticket
      if (updateData.status === 'resolved' || updateData.status === 'closed') {
        updatePayload.has_overtime = updateData.has_overtime || false;
        if (updateData.has_overtime) {
          updatePayload.overtime_type = updateData.overtime_type;
          // Only send overtime_duration for time-based overtime types
          const nonTimeOvertimeTypes = ['holiday', 'travel_over_100km'];
          if (!nonTimeOvertimeTypes.includes(updateData.overtime_type)) {
            updatePayload.overtime_duration = updateData.overtime_duration;
          }
          updatePayload.overtime_notes = updateData.overtime_notes || '';
        }
      }

      // Add rating fields (only if user can rate)
      if (updateData.rating > 0) {
        updatePayload.rating = updateData.rating;
        updatePayload.rating_notes = updateData.rating_notes || '';
      }

      // Add exceptional work fields
      updatePayload.has_exceptional_work = updateData.has_exceptional_work || false;
      if (updateData.has_exceptional_work) {
        updatePayload.work_type = updateData.work_type;
        updatePayload.work_description = updateData.work_description || '';
        if (updateData.work_hours) {
          updatePayload.work_hours = updateData.work_hours;
        }
        if (updateData.estimated_cost) {
          updatePayload.estimated_cost = updateData.estimated_cost;
        }
      }
      await ticketService.update(id, updatePayload);
      await fetchTicket();
      alert('تم تحديث التذكرة بنجاح');
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'فشل تحديث التذكرة');
    } finally {
      setSubmitting(false);
    }
  }, [id, updateData, fetchTicket]);

  const handleDeleteTicket = useCallback(async () => {
    if (!window.confirm('هل أنت متأكد من حذف هذه التذكرة؟ لا يمكن التراجع عن هذا الإجراء.')) return;
    setSubmitting(true);
    try {
      await ticketService.delete(id);
      alert('تم حذف التذكرة');
      navigate('/tickets');
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'فشل حذف التذكرة');
    } finally {
      setSubmitting(false);
    }
  }, [id, navigate]);

  const handleSendResolutionEmail = useCallback(async () => {
    if (!ticket) return;
    setSubmitting(true);
    try {
      await ticketService.sendResolutionEmail(id, resolutionEmailData);
      await fetchTicket();
      setShowResolutionEmailModal(false);
      alert('تم إرسال بريد الحل بنجاح');
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'فشل إرسال بريد الحل');
    } finally {
      setSubmitting(false);
    }
  }, [id, ticket, resolutionEmailData, fetchTicket]);

  const handleAssignmentChanged = useCallback(() => {
    fetchTicket();
  }, [fetchTicket]);

  // ── Derived state — memoized ───────────────────────────
  const currentUserRole = useMemo(() => normalizeRole(currentUser?.role), [currentUser?.role]);
  
  // Check if user can modify ticket (admin, supervisor, technician but not viewer)
  const canModifyTicket = useMemo(() => {
    return canWriteTickets(currentUser?.role) && !isViewer(currentUser?.role);
  }, [currentUser?.role]);

  // Permission logic for ticket assignment:
  // - Admin/Supervisor: CAN assign/remove technicians, CAN replace assignments
  // - Technician: CAN ONLY self-assign if ticket is unassigned, CANNOT assign others
  const canManageAssignees = useMemo(() => {
    // Admin and supervisor can fully manage ticket assignees.
    if ([ROLE.ADMIN, ROLE.SUPERVISOR].includes(currentUserRole)) {
      return true;
    }
    // Technicians cannot manage other assignees (only view).
    return false;
  }, [currentUserRole]);

  // Check if technician can self-assign (only when ticket is unassigned)
  const canSelfAssign = useMemo(() => {
    if (currentUserRole !== ROLE.TECHNICIAN) return false;
    // Can only self-assign if ticket is unassigned
    const hasAssignment = ticket?.assigned_to || (assignedUsers && assignedUsers.length > 0);
    return !hasAssignment;
  }, [assignedUsers, currentUserRole, ticket?.assigned_to]);

  // Check if current user is already assigned to this ticket
  const isAssignedToCurrentUser = useMemo(() => {
    if (!currentUser?.id) return false;
    return assignedUsers?.some(t => t.id === currentUser.id) ||
           ticket?.assigned_to === currentUser.id;
  }, [assignedUsers, currentUser?.id, ticket?.assigned_to]);

  // ── Loading / Not found ────────────────────────────────
  if (loading) return <LoadingSpinner />;
  if (!ticket) return (
    <Container fluid className="p-0">
      <Card>
        <Card.Body className="text-center py-5">
          <FaExclamationTriangle size={48} className="text-muted mb-3" />
          <h4>التذكرة غير موجودة</h4>
          <Link to="/tickets">
            <Button variant="primary" className="mt-3">العودة للقائمة</Button>
          </Link>
        </Card.Body>
      </Card>
    </Container>
  );

  return (
    <Container fluid className="p-0 ticket-detail-container">
      {/* Header Area */}
      <div className="ticket-detail-header">
        <Link to="/tickets" className="ticket-back-btn">
          <FaArrowRight />
          <span>العودة</span>
        </Link>
        <div className="ticket-header-title-area">
          <h1 className="ticket-header-id">تذكرة #{ticket.ticket_number}</h1>
          <p className="text-muted small mb-0 ticket-header-date">تم إنشاؤها في {formatDateTime(ticket.created_at)}</p>
        </div>
        <div className="ticket-header-actions">
          {canModifyTicket && (
            <Button
              variant="outline-primary"
              size="sm"
              className="rounded-pill px-3 me-2"
              onClick={handleOpenTicketEdit}
            >
              <FaEdit className="me-1" /> تعديل
            </Button>
          )}
          <StatusBadge status={ticket.status} />
          {canModifyTicket && ['admin', 'supervisor'].includes(currentUserRole) &&
            ['resolved', 'closed'].includes(ticket.status) && (
              <Button
                variant="outline-primary"
                size="sm"
                className="rounded-pill px-3"
                onClick={() => {
                  setResolutionEmailData({
                    resolution_summary: ticket.resolution_notes || '',
                    closure_note: '',
                    include_solution_attachment: !!ticket.solution_attachment
                  });
                  setShowResolutionEmailModal(true);
                }}
              >
                <FaEnvelopeOpenText className="me-1" /> إرسال الحل بالبريد
              </Button>
          )}
          {canModifyTicket && ['admin', 'supervisor'].includes(currentUserRole) && (
            <Button
              variant="outline-danger"
              size="sm"
              className="rounded-pill"
              onClick={handleDeleteTicket}
              disabled={submitting}
            >
              <FaTrashAlt />
            </Button>
          )}
        </div>
      </div>

      <Row className="ticket-detail-layout g-4">
        {/* Main Workspace - 8/12 */}
        <Col lg={8} className="ticket-main-column">
          {/* Main Description Card */}
          <div className="ticket-card-premium">
            <div className="ticket-card-premium-header">
              <h5 className="ticket-card-premium-title">
                <FaEnvelopeOpenText className="text-primary" />
                تفاصيل الطلب
              </h5>
            </div>
            <div className="ticket-card-premium-body">
              <div className="ticket-desc-content">
                {ticket.short_description}
              </div>

              {(ticket.resolution_notes || ticket.resolution_summary || ticket.has_overtime || ticket.solution_attachment) && (
                <div className="ticket-res-content">
                  {(ticket.resolution_notes || ticket.resolution_summary) && (
                    <>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FaCheckCircle className="text-success" />
                        <strong>ملاحظات الحل:</strong>
                      </div>
                      {ticket.resolution_notes || ticket.resolution_summary}
                      {ticket.resolved_by_name && (
                        <div className="mt-3 pt-2 border-top border-success border-opacity-10 small opacity-75">
                          تم الحل بواسطة: {ticket.resolved_by_name}
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Overtime Display */}
                  {ticket.has_overtime && (
                    <div className="mt-3 p-3 border rounded bg-warning bg-opacity-10">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FaClock className="text-warning" />
                        <strong>وقت إضافي</strong>
                      </div>
                      <div className="small">
                        <div><strong>النوع:</strong> {
                          ticket.overtime_type === 'after_hours' ? 'بعد ساعات العمل الرسمية' :
                          ticket.overtime_type === 'holiday' ? 'عطلة رسمية / عطلة عيد' :
                          ticket.overtime_type === 'travel_over_100km' ? 'تنقل أكثر من 100 كم' :
                          ticket.overtime_type
                        }</div>
                        {ticket.overtime_duration && (
                          <div><strong>المدة:</strong> {ticket.overtime_duration}</div>
                        )}
                        {ticket.overtime_notes && (
                          <div><strong>ملاحظات:</strong> {ticket.overtime_notes}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rating Display */}
                  {ticket.rating && (
                    <div className="mt-3 p-3 border rounded bg-info bg-opacity-10">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FaStar className="text-info" />
                        <strong>التقييم</strong>
                      </div>
                      <div className="small">
                        <div className="mb-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span key={star} style={{ fontSize: '1.2rem', color: star <= ticket.rating ? '#ffc107' : '#ddd' }}>★</span>
                          ))}
                        </div>
                        {ticket.rating_notes && (
                          <div><strong>ملاحظات:</strong> {ticket.rating_notes}</div>
                        )}
                        {ticket.rated_by_name && (
                          <div className="text-muted"><small>بواسطة: {ticket.rated_by_name} - {formatDateTime(ticket.rated_at)}</small></div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Exceptional Work Display */}
                  {ticket.has_exceptional_work && (
                    <div className="mt-3 p-3 border rounded bg-danger bg-opacity-10">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FaExclamationTriangle className="text-danger" />
                        <strong>عمل استثنائي</strong>
                      </div>
                      <div className="small">
                        {ticket.work_type && (
                          <div><strong>النوع:</strong> {
                            ticket.work_type === 'emergency' ? 'عمل طوارئ عاجل' :
                            ticket.work_type === 'extra_work' ? 'عمل إضافي غير متوقع' :
                            ticket.work_type === 'special_material' ? 'مواد خاصة / استثنائية' :
                            ticket.work_type === 'external_contractor' ? 'مقاول خارجي' :
                            ticket.work_type === 'other' ? 'أخرى' :
                            ticket.work_type
                          }</div>
                        )}
                        {ticket.work_description && (
                          <div><strong>الوصف:</strong> {ticket.work_description}</div>
                        )}
                        {ticket.work_hours && (
                          <div><strong>عدد الساعات:</strong> {ticket.work_hours}</div>
                        )}
                        {ticket.estimated_cost && (
                          <div><strong>التكلفة التقديرية:</strong> {ticket.estimated_cost} </div>
                        )}
                        {ticket.approved_by_name && (
                          <div className="text-muted"><small>معتمد بواسطة: {ticket.approved_by_name} - {formatDateTime(ticket.approved_at)}</small></div>
                        )}
                      </div>
                    </div>
                  )}

                  {ticket.solution_attachment && (
                    <div className="mt-3">
                       <Button variant="success" size="sm" className="rounded-pill" onClick={handleSecureSolutionDownload}>
                        <FaDownload className="me-1" /> تحميل مستند الحل
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Comments & Discussion */}
          <div className="ticket-card-premium">
            <div className="ticket-card-premium-header">
              <h5 className="ticket-card-premium-title">
                <FaUsers className="text-primary" />
                المناقشة والتعليقات
              </h5>
              <Badge bg="light" text="dark" className="rounded-pill border">{formatNumber(ticket.comments?.length || 0)}</Badge>
            </div>
            <div className="ticket-card-premium-body">
              <div className="comments-feed mb-4">
                {ticket.comments && ticket.comments.length > 0 ? (
                  ticket.comments.map(comment => (
                    <div key={comment.id} className="comment-card">
                      <div className="comment-card-header">
                        <div className="comment-user-info">
                          <div className="tech-member-avatar" style={{width: 24, height: 24, fontSize: '0.7rem'}}>
                            {comment.user_name?.charAt(0)}
                          </div>
                          <span>{comment.user_name}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {comment.updated_at && (
                            <span className="comment-edited-badge text-muted small">
                              (تم التعديل)
                            </span>
                          )}
                          <span className="comment-date">{formatDateTime(comment.created_at)}</span>
                          {canModifyTicket && (comment.user_id === currentUser?.id || currentUserRole === 'admin' || currentUserRole === 'supervisor') && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-primary"
                              onClick={() => {
                                setEditingCommentId(comment.id);
                                setEditingCommentText(comment.comment_text);
                              }}
                              title="تعديل"
                            >
                              <FaEdit size={12} />
                            </Button>
                          )}
                        </div>
                      </div>
                      {editingCommentId === comment.id ? (
                        <div className="comment-edit-form mt-2">
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={editingCommentText}
                            onChange={(e) => setEditingCommentText(e.target.value)}
                            className="sidebar-control mb-2"
                            style={{resize: 'none'}}
                          />
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              className="rounded-pill"
                              onClick={() => handleEditComment(comment.id)}
                              disabled={commentSubmitting || !editingCommentText.trim()}
                            >
                              <FaSave /> حفظ
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="rounded-pill"
                              onClick={handleCancelEditComment}
                              disabled={commentSubmitting}
                            >
                              <FaTimes /> إلغاء
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="comment-text">{comment.comment_text}</div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-muted small">لا توجد تعليقات بعد</div>
                )}
              </div>
              
              <Form onSubmit={handleAddComment}>
                <div className="ticket-comment-composer d-flex gap-2">
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="أضف رداً على هذه التذكرة..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    className="sidebar-control"
                    style={{resize: 'none'}}
                  />
                  <Button variant="primary" type="submit" className="rounded-pill px-4" disabled={submitting || !commentText.trim()}>
                    <FaPaperPlane />
                  </Button>
                </div>
              </Form>
            </div>
          </div>

          {/* Attachments Section */}
          <div className="ticket-card-premium">
            <div className="ticket-card-premium-header">
              <h5 className="ticket-card-premium-title">
                <FaPaperclip className="text-primary" />
                المرفقات والوثائق
              </h5>
              <Badge bg="light" text="dark" className="rounded-pill border">{formatNumber(ticket.attachments?.length || 0)}</Badge>
            </div>
            <div className="ticket-card-premium-body">
              <div className="attachments-grid mb-4">
                {ticket.attachments && ticket.attachments.length > 0 ? (
                  ticket.attachments.map(attachment => (
                    <div key={attachment.id} className="attachment-file-card">
                       <div className="d-flex align-items-center gap-2 overflow-hidden">
                          <FaPaperclip className="text-muted" />
                          <div className="text-truncate small fw-bold" title={attachment.file_name}>{attachment.file_name}</div>
                       </div>
                       <Button variant="link" size="sm" className="p-0 text-primary text-decoration-none mt-2 small ms-auto" onClick={() => handleSecureAttachmentDownload(attachment.id)}>
                         تحميل
                       </Button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-3 text-muted small">لا توجد مرفقات</div>
                )}
              </div>

              <div className="pt-3 border-top">
                <Form onSubmit={handleFileUpload} className="ticket-attachment-upload row g-2 align-items-end">
                  <Col md={5}>
                    <label className="sidebar-field-label">إضافة مرفق جديد</label>
                    <Form.Control type="file" size="sm" className="sidebar-control" onChange={(e) => setSelectedFile(e.target.files[0])} />
                  </Col>
                  <Col md={4}>
                    <label className="sidebar-field-label">تصنيف الملف</label>
                    <Form.Select size="sm" className="sidebar-control" value={fileType} onChange={(e) => setFileType(e.target.value)}>
                      <option value="attachment">مرفق عام</option>
                      <option value="solution">مستند حل</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Button variant="outline-primary" size="sm" type="submit" className="w-100 rounded-pill" disabled={submitting || !selectedFile}>
                      رفع الملف
                    </Button>
                  </Col>
                </Form>
              </div>
            </div>
          </div>
        </Col>

        {/* Sidebar - 4/12 */}
        <Col lg={4} className="ticket-sidebar-column">
          {/* Metadata Card */}
          <div className="ticket-card-premium">
            <div className="ticket-card-premium-header">
              <h5 className="ticket-card-premium-title">معلومات التذكرة</h5>
            </div>
            <div className="ticket-card-premium-body">
              <div className="sidebar-info-list">
                <div className="sidebar-info-item">
                  <span className="sidebar-info-label">المكان / الفرع</span>
                  <span className="sidebar-info-value">
                    <FaBuilding className="text-muted" />
                    {ticket.branch_name}
                  </span>
                </div>
                <div className="sidebar-info-item">
                  <span className="sidebar-info-label">الأولوية</span>
                  <span className="sidebar-info-value">
                    <PriorityBadge priority={ticket.priority} />
                  </span>
                </div>
                <div className="sidebar-info-item">
                  <span className="sidebar-info-label">حالة SLA</span>
                  <span className="sidebar-info-value">
                    <SlaStatusBadge slaStatus={ticket.sla_status} />
                  </span>
                </div>
                {ticket.due_at && (
                  <div className="sidebar-info-item">
                    <span className="sidebar-info-label">موعد الانتهاء</span>
                    <span className="sidebar-info-value">
                      <FaClock className="text-muted" />
                      {formatDateTime(ticket.due_at)}
                    </span>
                  </div>
                )}
                {ticket.first_response_at && (
                  <div className="sidebar-info-item">
                    <span className="sidebar-info-label">أول رد</span>
                    <span className="sidebar-info-value">
                      <FaClock className="text-muted" />
                      {formatDateTime(ticket.first_response_at)}
                    </span>
                  </div>
                )}
                <div className="sidebar-info-item">
                  <span className="sidebar-info-label">نوع الطلب</span>
                  <span className="sidebar-info-value">{translateIssueTypes(ticket.issue_types)}</span>
                </div>
                <div className="sidebar-info-item">
                  <span className="sidebar-info-label">تاريخ الاستلام</span>
                  <span className="sidebar-info-value">
                    <FaCalendarAlt className="text-muted" />
                    {formatDateTime(ticket.received_date)}
                  </span>
                </div>
                {/* "بواسطة" field - show resolved_by if resolved/closed, otherwise assigned_to, otherwise unassigned */}
                {((ticket.status === 'resolved' || ticket.status === 'closed') && ticket.resolved_by_name) ? (
                  <div className="sidebar-info-item">
                    <span className="sidebar-info-label">بواسطة</span>
                    <span className="sidebar-info-value">{ticket.resolved_by_name}</span>
                  </div>
                ) : ticket.assigned_to_name ? (
                  <div className="sidebar-info-item">
                    <span className="sidebar-info-label">بواسطة</span>
                    <span className="sidebar-info-value">{ticket.assigned_to_name}</span>
                  </div>
                ) : (
                  <div className="sidebar-info-item">
                    <span className="sidebar-info-label">بواسطة</span>
                    <span className="sidebar-info-value text-muted">غير مسندة</span>
                  </div>
                )}

                {/* Overtime Information Section - Always Visible */}
                <div className="sidebar-info-item overtime-section">
                  <span className="sidebar-info-label">
                    <FaClock className="text-muted ms-1" />
                    الوقت الإضافي
                  </span>
                  <span className="sidebar-info-value">
                    {ticket.has_overtime ? (
                      <div className="overtime-details">
                        <div className="overtime-status">
                          <Badge bg="warning" text="dark" className="rounded-pill">
                            يوجد وقت إضافي
                          </Badge>
                        </div>
                        <div className="overtime-info-list mt-2">
                          <div className="overtime-info-item">
                            <span className="overtime-label">النوع:</span>
                            <span className="overtime-value">
                              {ticket.overtime_type === 'after_hours' ? 'بعد ساعات العمل الرسمية' :
                               ticket.overtime_type === 'holiday' ? 'عطلة رسمية / عطلة عيد' :
                               ticket.overtime_type === 'travel_over_100km' ? 'تنقل أكثر من 100 كم' :
                               ticket.overtime_type || '-'}
                            </span>
                          </div>
                          {ticket.overtime_duration && (
                            <div className="overtime-info-item">
                              <span className="overtime-label">المدة:</span>
                              <span className="overtime-value">{ticket.overtime_duration}</span>
                            </div>
                          )}
                          {ticket.overtime_notes && (
                            <div className="overtime-info-item">
                              <span className="overtime-label">ملاحظات:</span>
                              <span className="overtime-value">{ticket.overtime_notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <Badge bg="secondary" className="rounded-pill">
                        لا يوجد وقت إضافي
                      </Badge>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Technicians Card — new inline multi-select */}
          {canManageAssignees ? (
            <TicketAssigneeField
              assignableUsers={assignableUsers}
              assignedUsers={assignedUsers}
              ticketId={id}
              onAssignmentChanged={handleAssignmentChanged}
              canManage={canManageAssignees}
              submitting={submitting}
            />
          ) : (
            // Technician view: show assigned technicians and self-assign button if applicable
            <div className="ticket-card-premium">
              <div className="ticket-card-premium-header">
                <h5 className="ticket-card-premium-title">
                  <FaUsers className="text-primary" style={{fontSize: '0.9rem'}} />
                  فريق العمل
                </h5>
                {assignedUsers && assignedUsers.length > 0 && (
                  <Badge bg="light" text="dark" className="rounded-pill border" style={{fontSize: '0.72rem'}}>
                    {assignedUsers.length}
                  </Badge>
                )}
              </div>
              <div className="ticket-card-premium-body" style={{padding: '1rem 1.25rem'}}>
                {/* Show assigned technicians (read-only) */}
                {assignedUsers && assignedUsers.length > 0 ? (
                  <div className="td-tech-chips">
                    {assignedUsers.map((tech) => (
                      <div key={tech.id} className="td-tech-chip">
                        <div className="td-tech-chip-avatar">{(tech.full_name || '?').charAt(0)}</div>
                        <div className="td-tech-chip-info">
                          <span className="td-tech-chip-name">{tech.full_name}</span>
                          <span className="td-tech-chip-role">
                            {[getRoleLabel(tech.role), tech.username ? `@${tech.username}` : null].filter(Boolean).join(' • ')}
                          </span>
                          {tech.is_primary && (
                            <span className="td-tech-chip-badge">أساسي</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0">لا يوجد فريق عمل معين لهذه التذكرة</p>
                )}
                
                {/* Self-assign button for technicians when ticket is unassigned */}
                {canSelfAssign && !isAssignedToCurrentUser && currentUserRole === ROLE.TECHNICIAN && (
                  <div className="mt-3">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={async () => {
                        if (!window.confirm('هل تريد قبول هذه التذكرة؟')) return;
                        try {
                          await ticketService.selfAssign(id);
                          handleAssignmentChanged();
                          alert('تم تعيينك للتذكرة بنجاح');
                        } catch (err) {
                          console.error('Self-assign error:', err);
                          alert(err.response?.data?.error || 'فشل التعيين الذاتي');
                        }
                      }}
                    >
                      <FaUserPlus className="ms-1" />
                      قبول التذكرة
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Update Action Panel */}
          <div className="ticket-card-premium sidebar-action-card">
            <div className="ticket-card-premium-header">
              <h5 className="ticket-card-premium-title">تحديث الحالة</h5>
            </div>
            <div className="ticket-card-premium-body">
              <div className="sidebar-action-form">
                <div>
                  <label className="sidebar-field-label">الحالة الحالية</label>
                  <Form.Select 
                    value={updateData.status} 
                    onChange={(e) => setUpdateData(prev => ({ ...prev, status: e.target.value }))}
                    className="sidebar-control"
                  >
                    <option value="new">جديدة</option>
                    <option value="in_progress">قيد العمل</option>
                    <option value="on_hold">معلقة</option>
                    <option value="resolved">محلولة</option>
                  </Form.Select>
                </div>

                <div>
                  <label className="sidebar-field-label">تعديل الأولوية</label>
                  <Form.Select 
                    value={updateData.priority} 
                    onChange={(e) => setUpdateData(prev => ({ ...prev, priority: e.target.value }))}
                    className="sidebar-control"
                  >
                    <option value="low">منخفضة</option>
                    <option value="medium">متوسطة</option>
                    <option value="high">عالية</option>
                    <option value="urgent">عاجلة</option>
                  </Form.Select>
                </div>

                {updateData.status === 'resolved' && (
                  <div className="animate-in">
                    <label className="sidebar-field-label">ملاحظات الحل</label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="كيف تم حل المشكلة؟"
                      value={updateData.resolution_summary || ''}
                      onChange={(e) => setUpdateData(prev => ({ ...prev, resolution_summary: e.target.value }))}
                      className="sidebar-control mb-2"
                    />
                    <div className="row g-2">
                       <Col xs={6}>
                          <Form.Control type="date" size="sm" className="sidebar-control" value={updateData.custom_resolved_date || ''} onChange={(e) => setUpdateData(prev => ({ ...prev, custom_resolved_date: e.target.value }))} />
                       </Col>
                       <Col xs={6}>
                          <Form.Control type="time" size="sm" className="sidebar-control" value={updateData.custom_resolved_time || ''} onChange={(e) => setUpdateData(prev => ({ ...prev, custom_resolved_time: e.target.value }))} />
                       </Col>
                    </div>

                    {/* Overtime Section */}
                    <div className="mt-3 p-3 border rounded bg-light">
                      <div className="form-check mb-2">
                        <Form.Check
                          type="checkbox"
                          id="has_overtime"
                          label="يتضمن وقت إضافي"
                          checked={updateData.has_overtime || false}
                          onChange={(e) => setUpdateData(prev => ({ 
                            ...prev, 
                            has_overtime: e.target.checked,
                            overtime_type: e.target.checked ? prev.overtime_type : '',
                            overtime_duration: e.target.checked ? prev.overtime_duration : '',
                            overtime_notes: e.target.checked ? prev.overtime_notes : ''
                          }))}
                        />
                      </div>
                      
                      {updateData.has_overtime && (
                        <div className="animate-in">
                          <label className="sidebar-field-label">نوع الوقت الإضافي</label>
                          <Form.Select
                            value={updateData.overtime_type || ''}
                            onChange={(e) => setUpdateData(prev => ({ 
                              ...prev, 
                              overtime_type: e.target.value,
                              // Clear duration when selecting non-time overtime types
                              overtime_duration: ['holiday', 'travel_over_100km'].includes(e.target.value) ? '' : prev.overtime_duration
                            }))}
                            className="sidebar-control mb-2"
                          >
                            <option value="">اختر النوع...</option>
                            <option value="after_hours">بعد ساعات العمل الرسمية</option>
                            <option value="holiday">عطلة رسمية / عطلة عيد</option>
                            <option value="travel_over_100km">تنقل أكثر من 100 كم</option>
                          </Form.Select>
                          
                          {/* Show duration field only for time-based overtime types */}
                          {updateData.overtime_type && !['holiday', 'travel_over_100km'].includes(updateData.overtime_type) && (
                            <>
                              <label className="sidebar-field-label">مدة الوقت الإضافي</label>
                              <Form.Control
                                type="text"
                                placeholder="مثال: 2 ساعة"
                                value={updateData.overtime_duration || ''}
                                onChange={(e) => setUpdateData(prev => ({ ...prev, overtime_duration: e.target.value }))}
                                className="sidebar-control mb-2"
                              />
                            </>
                          )}
                          
                          <label className="sidebar-field-label">ملاحظات الوقت الإضافي (اختياري)</label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="أسباب الوقت الإضافي..."
                            value={updateData.overtime_notes || ''}
                            onChange={(e) => setUpdateData(prev => ({ ...prev, overtime_notes: e.target.value }))}
                            className="sidebar-control"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Rating Section - Only visible to supervisors and admins */}
                {canRateTechnician(currentUser?.role) && (
                  <div className="sidebar-section mt-3">
                    <div className="sidebar-section-header">
                      <FaStar className="ms-1" style={{ fontSize: '0.85rem' }} />
                      تقييم الأداء الفني
                    </div>
                    <div className="sidebar-section-body">
                      <div className="rating-stars-container mb-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            onClick={() => setUpdateData(prev => ({ ...prev, rating: star }))}
                            style={{
                              cursor: 'pointer',
                              fontSize: '1.5rem',
                              color: star <= updateData.rating ? '#ffc107' : '#ddd',
                              marginLeft: '5px'
                            }}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                      <label className="sidebar-field-label">ملاحظات التقييم (اختياري)</label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="ملاحظات حول أداء فريق العمل..."
                        value={updateData.rating_notes || ''}
                        onChange={(e) => setUpdateData(prev => ({ ...prev, rating_notes: e.target.value }))}
                        className="sidebar-control"
                      />
                    </div>
                  </div>
                )}

                {/* Exceptional Work Section - Only visible to supervisors and admins */}
                {canManageExceptionalWork(currentUser?.role) && (
                  <div className="sidebar-section mt-3">
                    <div className="sidebar-section-header">
                      <FaExclamationTriangle className="ms-1" style={{ fontSize: '0.85rem' }} />
                      الأعمال الاستثنائية
                    </div>
                    <div className="sidebar-section-body">
                      <Form.Check
                        type="checkbox"
                        id="has_exceptional_work"
                        label="توجد أعمال استثنائية"
                        checked={updateData.has_exceptional_work || false}
                        onChange={(e) => setUpdateData(prev => ({ 
                          ...prev, 
                          has_exceptional_work: e.target.checked,
                          work_type: e.target.checked ? prev.work_type : '',
                          work_description: e.target.checked ? prev.work_description : '',
                          work_hours: e.target.checked ? prev.work_hours : '',
                          estimated_cost: e.target.checked ? prev.estimated_cost : ''
                        }))}
                        className="mb-2"
                      />

                      {updateData.has_exceptional_work && (
                        <div className="animate-in">
                          <label className="sidebar-field-label">نوع العمل الاستثنائي <span className="text-danger">*</span></label>
                          <Form.Select
                            value={updateData.work_type || ''}
                            onChange={(e) => setUpdateData(prev => ({ 
                              ...prev, 
                              work_type: e.target.value
                            }))}
                            className="sidebar-control mb-2"
                            required
                          >
                            <option value="">اختر النوع...</option>
                            <option value="emergency">عمل طوارئ عاجل</option>
                            <option value="extra_work">عمل إضافي غير متوقع</option>
                            <option value="special_material">مواد خاصة / استثنائية</option>
                            <option value="external_contractor">مقاول خارجي</option>
                            <option value="other">أخرى</option>
                          </Form.Select>
                          
                          <label className="sidebar-field-label">الوصف / الملاحظات <span className="text-danger">*</span></label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="وصف العمل الاستثنائي..."
                            value={updateData.work_description || ''}
                            onChange={(e) => setUpdateData(prev => ({ ...prev, work_description: e.target.value }))}
                            className="sidebar-control mb-2"
                            required
                          />

                          <label className="sidebar-field-label">عدد الساعات</label>
                          <Form.Control
                            type="number"
                            step="0.5"
                            min="0"
                            placeholder="مثال: 2.5"
                            value={updateData.work_hours || ''}
                            onChange={(e) => setUpdateData(prev => ({ ...prev, work_hours: e.target.value }))}
                            className="sidebar-control mb-2"
                          />

                          <label className="sidebar-field-label">التكلفة التقديرية</label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="مثال: 150.00"
                            value={updateData.estimated_cost || ''}
                            onChange={(e) => setUpdateData(prev => ({ ...prev, estimated_cost: e.target.value }))}
                            className="sidebar-control mb-2"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Update Button - Show for users who can modify tickets */}
                {canModifyTicket && (
                  <div className="form-actions ticket-update-action-bar mt-4">
                    <Button 
                      variant="primary" 
                      className="btn-action-save" 
                      onClick={handleUpdateTicket}
                      disabled={submitting}
                    >
                      <FaSave className="btn-icon" />
                      {submitting ? 'جارٍ التحديث...' : 'تحديث التذكرة'}
                    </Button>
                    
                    <Button
                      variant="outline-secondary"
                      className="btn-action-view"
                      onClick={() => navigate('/tickets')}
                      disabled={submitting}
                    >
                      <FaArrowRight className="btn-icon" />
                      العودة للقائمة
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Resolution Email Modal — only modal left (small, focused) */}
      <Modal 
        show={showResolutionEmailModal} 
        onHide={() => { if (!submitting) setShowResolutionEmailModal(false); }} 
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">إرسال بريد الحل</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form>
            <Form.Group className="mb-3">
              <label className="sidebar-field-label">ملخص الحل للعميل</label>
              <Form.Control
                as="textarea"
                rows={3}
                value={resolutionEmailData.resolution_summary}
                onChange={(e) => setResolutionEmailData(prev => ({ ...prev, resolution_summary: e.target.value }))}
                className="sidebar-control"
                disabled={submitting}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <label className="sidebar-field-label">ملاحظة إضافية</label>
              <Form.Control
                as="textarea"
                rows={2}
                value={resolutionEmailData.closure_note}
                onChange={(e) => setResolutionEmailData(prev => ({ ...prev, closure_note: e.target.value }))}
                className="sidebar-control"
                disabled={submitting}
              />
            </Form.Group>
            <Form.Check
              type="checkbox"
              id="include-solution"
              label="إرفاق وثيقة إتمام الحل"
              checked={resolutionEmailData.include_solution_attachment}
              onChange={(e) => setResolutionEmailData(prev => ({ ...prev, include_solution_attachment: e.target.checked }))}
              className="small fw-600"
              disabled={submitting}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-4">
          <Button 
            variant="light" 
            className="rounded-pill px-4" 
            onClick={() => { if (!submitting) setShowResolutionEmailModal(false); }}
            disabled={submitting}
          >
            إلغاء
          </Button>
          <Button variant="primary" className="rounded-pill px-4" onClick={handleSendResolutionEmail} disabled={submitting}>إرسال البريد</Button>
        </Modal.Footer>
      </Modal>
      {/* Ticket Edit Modal */}
      <Modal show={showTicketEditModal} onHide={() => setShowTicketEditModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>تعديل التذكرة</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>عنوان التذكرة</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={ticketEditData.short_description}
                onChange={(e) => setTicketEditData({ ...ticketEditData, short_description: e.target.value })}
                className="sidebar-control"
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>الفرع</Form.Label>
                  <Form.Select
                    value={ticketEditData.branch_id}
                    onChange={(e) => setTicketEditData({ ...ticketEditData, branch_id: e.target.value })}
                    className="sidebar-control"
                  >
                    <option value="">اختر الفرع...</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>الأولوية</Form.Label>
                  <Form.Select
                    value={ticketEditData.priority}
                    onChange={(e) => setTicketEditData({ ...ticketEditData, priority: e.target.value })}
                    className="sidebar-control"
                  >
                    <option value="">اختر...</option>
                    <option value="low">منخفضة الأولوية</option>
                    <option value="medium">أولوية متوسطة</option>
                    <option value="high">أولوية عالية</option>
                    <option value="urgent">أولوية قصوى</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>موعد الاستلام</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control 
                      type="date" 
                      value={ticketEditData.received_date} 
                      onChange={(e) => setTicketEditData({ ...ticketEditData, received_date: e.target.value })}
                      className="sidebar-control"
                    />
                    <Form.Control 
                      type="time" 
                      value={ticketEditData.received_time} 
                      onChange={(e) => setTicketEditData({ ...ticketEditData, received_time: e.target.value })}
                      className="sidebar-control"
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>الحالة</Form.Label>
                  <Form.Select
                    value={ticketEditData.status}
                    onChange={(e) => setTicketEditData({ ...ticketEditData, status: e.target.value })}
                    className="sidebar-control"
                  >
                    <option value="">اختر...</option>
                    <option value="new">تذكرة جديدة</option>
                    <option value="in_progress">قيد المتابعة</option>
                    <option value="on_hold">متوقفة مؤقتاً</option>
                    <option value="resolved">محلولة</option>
                    <option value="closed">مغلقة كلياً</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {(ticketEditData.status === 'resolved' || ticketEditData.status === 'closed') && (
              <Row className="animate-in">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>موعد الانتهاء</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control 
                        type="date" 
                        value={ticketEditData.resolved_date} 
                        onChange={(e) => setTicketEditData({ ...ticketEditData, resolved_date: e.target.value })}
                        className="sidebar-control"
                      />
                      <Form.Control 
                        type="time" 
                        value={ticketEditData.resolved_time} 
                        onChange={(e) => setTicketEditData({ ...ticketEditData, resolved_time: e.target.value })}
                        className="sidebar-control"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ملاحظات الحل</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      value={ticketEditData.resolution_notes}
                      onChange={(e) => setTicketEditData({ ...ticketEditData, resolution_notes: e.target.value })}
                      placeholder="كيف تم حل المشكلة؟"
                      className="sidebar-control"
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Form.Group className="mb-3">
              <Form.Label>نوع المشكلة</Form.Label>
              <Form.Control
                as="textarea"
                rows={1}
                value={ticketEditData.issue_types?.join(', ') || ''}
                onChange={(e) => setTicketEditData({ ...ticketEditData, issue_types: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                placeholder="أدخل أنواع المشاكل مفصولة بفواصل"
                className="sidebar-control"
              />
              <Form.Text className="text-muted small">
                مثال: صيانة حاسب, شبكات, طابعات
              </Form.Text>
            </Form.Group>

            {/* Overtime in Modal */}
            <div className="p-3 border rounded mb-3 bg-light-premium">
              <Form.Check
                type="checkbox"
                id="edit_has_overtime"
                label="وقت إضافي"
                checked={ticketEditData.has_overtime}
                onChange={(e) => setTicketEditData({ ...ticketEditData, has_overtime: e.target.checked })}
                className="mb-2 fw-bold"
              />
              {ticketEditData.has_overtime && (
                <Row className="animate-in g-2">
                  <Col md={4}>
                    <Form.Select
                      size="sm"
                      value={ticketEditData.overtime_type}
                      onChange={(e) => setTicketEditData({ ...ticketEditData, overtime_type: e.target.value })}
                      className="sidebar-control"
                    >
                      <option value="">النوع...</option>
                      <option value="after_hours">بعد العمل</option>
                      <option value="holiday">عطلة</option>
                      <option value="travel_over_100km">تنقل &gt; 100كم</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Control
                      size="sm"
                      placeholder="المدة"
                      value={ticketEditData.overtime_duration}
                      onChange={(e) => setTicketEditData({ ...ticketEditData, overtime_duration: e.target.value })}
                      className="sidebar-control"
                    />
                  </Col>
                  <Col md={5}>
                    <Form.Control
                      size="sm"
                      placeholder="ملاحظات"
                      value={ticketEditData.overtime_notes}
                      onChange={(e) => setTicketEditData({ ...ticketEditData, overtime_notes: e.target.value })}
                      className="sidebar-control"
                    />
                  </Col>
                </Row>
              )}
            </div>

            {/* Exceptional Work in Modal */}
            <div className="p-3 border rounded bg-light-premium">
              <Form.Check
                type="checkbox"
                id="edit_has_exceptional_work"
                label="أعمال استثنائية"
                checked={ticketEditData.has_exceptional_work}
                onChange={(e) => setTicketEditData({ ...ticketEditData, has_exceptional_work: e.target.checked })}
                className="mb-2 fw-bold"
              />
              {ticketEditData.has_exceptional_work && (
                <div className="animate-in">
                  <Row className="g-2 mb-2">
                    <Col md={6}>
                      <Form.Select
                        size="sm"
                        value={ticketEditData.work_type}
                        onChange={(e) => setTicketEditData({ ...ticketEditData, work_type: e.target.value })}
                        className="sidebar-control"
                      >
                        <option value="">نوع العمل...</option>
                        <option value="emergency">طوارئ</option>
                        <option value="extra_work">إضافي</option>
                        <option value="special_material">مواد خاصة</option>
                        <option value="other">أخرى</option>
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Form.Control
                        size="sm"
                        type="number"
                        placeholder="ساعات"
                        value={ticketEditData.work_hours}
                        onChange={(e) => setTicketEditData({ ...ticketEditData, work_hours: e.target.value })}
                        className="sidebar-control"
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Control
                        size="sm"
                        type="number"
                        placeholder="تكلفة"
                        value={ticketEditData.estimated_cost}
                        onChange={(e) => setTicketEditData({ ...ticketEditData, estimated_cost: e.target.value })}
                        className="sidebar-control"
                      />
                    </Col>
                  </Row>
                  <Form.Control
                    size="sm"
                    as="textarea"
                    rows={2}
                    placeholder="وصف العمل الاستثنائي..."
                    value={ticketEditData.work_description}
                    onChange={(e) => setTicketEditData({ ...ticketEditData, work_description: e.target.value })}
                    className="sidebar-control"
                  />
                </div>
              )}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 p-4">
          <Button 
            variant="light" 
            className="rounded-pill px-4" 
            onClick={() => setShowTicketEditModal(false)}
            disabled={ticketEditSubmitting}
          >
            إلغاء
          </Button>
          <Button 
            variant="primary" 
            className="rounded-pill px-4" 
            onClick={handleSaveTicketEdit}
            disabled={ticketEditSubmitting || !ticketEditData.short_description?.trim()}
          >
            {ticketEditSubmitting ? 'جاري الحفظ...' : 'حفظ التعديلات'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TicketDetail;
